import * as DateF from 'date-fns'
import Numbro from 'numbro'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { UserAvatar } from '@root/entities/auth/ui'
import { DATE_FORMAT } from '@root/shared'

type RankCardVariant = 'green' | 'blue'
type RankCardProps = {
  endContent: React.ReactNode
  rank: number | undefined
  variant: RankCardVariant
}

export const RankCard: React.FC<RankCardProps> = ({
  endContent,
  rank,
  variant,
}) => {
  const { t } = useTranslation()
  const dateFormatted = useMemo(
    () => DateF.format(new Date(), DATE_FORMAT.dd_MM_yy),
    [],
  )

  return (
    <Root>
      <UserAvatar size={50} />
      <Center>
        <DateContainer>{dateFormatted}</DateContainer>
        <PlaceContainer>
          {rank ? ( // 0 and undefined
            <>
              <PlaceText>{t('leaderboard.you_are_in')}</PlaceText>
              <Place $variant={variant}>
                {Numbro(rank).format({ output: 'ordinal' })}
              </Place>
              <PlaceText>{t('leaderboard.place')}</PlaceText>
            </>
          ) : (
            <PlaceText>{t('leaderboard.dont_have_rank')}</PlaceText>
          )}
        </PlaceContainer>
      </Center>
      <Profit>{endContent}</Profit>
    </Root>
  )
}

const Root = styled.div`
  background: var(--appearance-1);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;

  padding-inline: 12px;
  padding-block: 12px;
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto;
`
const VariantToColor: Record<RankCardVariant, string> = {
  green: 'var(--green-9)',
  blue: 'var(--blue-9)',
}
const Place = styled.div<{ $variant: RankCardVariant }>`
  height: 24px;
  padding-inline: 8px;
  display: flex;
  align-items: center;

  background: ${(x) => VariantToColor[x.$variant]};

  border-radius: 16px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  color: var(--text-12);
`

const PlaceText = styled.div`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
  line-height: 15px;
`

const PlaceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const DateContainer = styled.div`
  color: var(--text-10);
  font-size: 10px;
  font-weight: 600;
  line-height: 11px;
`

const Center = styled.div`
  padding-left: 12px;
`

const Profit = styled.div`
  color: var(--text-12);
  text-align: right;
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
`
